<template>
    <div
        class="control textarea"
        :style="controlStyle"
    >
        <label
            v-if="label"
            class="control__label control-label"
        >
            {{ label }}
            <span
                v-if="required"
                class="control__required"
            >*</span>
        </label>

        <v-textarea
            class="control__field"
            :auto-grow="autoGrow"
            :clearable="clearable"
            :counter="counter"
            :disabled="disabled"
            :hide-details="hideDetails"
            :maxlength="maxlength"
            :model-value="modelValue"
            :name="name || undefined"
            :placeholder="placeholder"
            :readonly="readonly"
            :rows="rows"
            :rules="validationRules"
            :density="density"
            rounded="lg"
            variant="outlined"
            @blur="$emit('blur', $event)"
            @focus="$emit('focus', $event)"
            @update:model-value="$emit('update:modelValue', $event)"
        />
    </div>
</template>

<script setup>
import { computed } from 'vue'

const props = defineProps({
  modelValue: {
    type: String,
    default: '',
  },

  label: {
    type: String,
    default: '',
  },

  placeholder: {
    type: String,
    default: '',
  },

  name: {
    type: String,
    default: '',
  },

  width: {
    type: [String, Number],
    default: '100%',
  },

  rows: {
    type: [String, Number],
    default: 4,
  },

  required: {
    type: Boolean,
    default: false,
  },

  disabled: {
    type: Boolean,
    default: false,
  },

  readonly: {
    type: Boolean,
    default: false,
  },

  clearable: {
    type: Boolean,
    default: false,
  },

  autoGrow: {
    type: Boolean,
    default: false,
  },

  counter: {
    type: [Boolean, Number, String],
    default: false,
  },

  maxlength: {
    type: [Number, String],
    default: null,
  },

  hideDetails: {
    type: [Boolean, String],
    default: 'auto',
  },

  density: {
    type: String,
    default: 'compact',
    validator: (value) => ['default', 'comfortable', 'compact'].includes(value),
  },
})

defineEmits(['update:modelValue', 'blur', 'focus'])

const controlStyle = computed(() => {
  const width = typeof props.width === 'number' ? `${props.width}px` : props.width

  return {
    '--form-control-width': width,
  }
})

const validationRules = computed(() => {
  if (!props.required) {
    return []
  }

  return [
    (value) => String(value ?? '').trim().length > 0 || 'Obavezno polje',
  ]
})
</script>

<style
    scoped
    lang="scss"
>
.textarea {
    --textarea-min-height: 169px;
    --textarea-border-color: var(--color-control-border);

    :deep(.v-field) {
        min-height: var(--textarea-min-height);
        background: var(--color-white);
    }

    :deep(.v-field__outline) {
        --v-field-border-width: 1px;
        --v-field-border-opacity: 1;
        color: var(--textarea-border-color);
    }

    :deep(.v-field__input) {
        min-height: var(--textarea-min-height);
        padding: 16px;
        color: var(--color-text-body);
        font-size: 16px;
        font-weight: 300;
        line-height: clamp-bp("sm", "xl", 20, 22);
    }

    :deep(textarea.v-field__input) {
        align-self: stretch;
        resize: none;
    }

    :deep(textarea.v-field__input::placeholder) {
        color: var(--color-text-placeholder);
        font-size: var(--placeholder-font-size);
        opacity: 1;
    }
}
</style>
